import { useState } from 'react';
import { motion } from 'framer-motion';
import { 
  Trophy, 
  Plus, 
  Search, 
  Settings2, 
  MoreVertical, 
  Trash2, 
  Play, 
  Pause,
  ExternalLink,
  Target,
  Gift
} from 'lucide-react';
import { Layout } from '@/components/layout/Layout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useGamification, GamificationCampaign } from '@/hooks/useGamification';
import { format } from 'date-fns';
import { CreateCampaignModal } from '@/components/gamification/CreateCampaignModal';
import { CampaignStepEditor } from '@/components/gamification/CampaignStepEditor';

export default function Gamification() {
  const { campaigns, isLoading, deleteCampaign, updateCampaign } = useGamification();
  const [searchQuery, setSearchQuery] = useState('');
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [editingCampaign, setEditingCampaign] = useState<GamificationCampaign | null>(null);

  const filteredCampaigns = campaigns.filter(c => 
    c.title.toLowerCase().includes(searchQuery.toLowerCase()) || 
    c.description?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const toggleCampaignStatus = (campaign: GamificationCampaign) => {
    updateCampaign.mutate({
      id: campaign.id,
      is_active: !campaign.is_active
    });
  };

  return (
    <Layout>
      <div className="container mx-auto px-4 py-8">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold mb-1 sm:mb-2 flex items-center gap-2">
              <Trophy className="h-8 w-8 text-yellow-500" />
              QR Gamification
            </h1>
            <p className="text-sm sm:text-base text-muted-foreground">
              Create interactive scavenger hunts and multi-step rewards
            </p>
          </div>
          <Button onClick={() => setIsCreateModalOpen(true)} className="gap-2">
            <Plus className="h-4 w-4" />
            Create Campaign
          </Button>
        </div>

        <Card className="mb-6">
          <div className="p-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input 
                placeholder="Search campaigns..." 
                className="pl-10"
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
              />
            </div>
          </div>
        </Card>

        {isLoading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[...Array(3)].map((_, i) => (
              <Card key={i} className="h-64 animate-pulse bg-muted/50" />
            ))}
          </div>
        ) : filteredCampaigns.length === 0 ? (
          <Card className="p-12 text-center">
            <div className="mx-auto w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mb-4">
              <Trophy className="h-8 w-8 text-primary" />
            </div>
            <h3 className="text-lg font-semibold mb-2">No campaigns yet</h3>
            <p className="text-muted-foreground mb-6 max-w-sm mx-auto">
              Engagement starts here. Create your first QR scavenger hunt to delight your audience.
            </p>
            <Button onClick={() => setIsCreateModalOpen(true)}>
              Get Started
            </Button>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredCampaigns.map((campaign) => (
              <motion.div
                key={campaign.id}
                layout
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
              >
                <Card className="h-full flex flex-col overflow-hidden hover:shadow-lg transition-shadow">
                  <div className="p-6 flex-1">
                    <div className="flex justify-between items-start mb-4">
                      <Badge variant={campaign.is_active ? "default" : "secondary"}>
                        {campaign.is_active ? "Active" : "Paused"}
                      </Badge>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon">
                            <MoreVertical className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => setEditingCampaign(campaign)}>
                            <Settings2 className="h-4 w-4 mr-2" /> Edit Campaign
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => toggleCampaignStatus(campaign)}>
                            {campaign.is_active ? (
                              <><Pause className="h-4 w-4 mr-2" /> Pause</>
                            ) : (
                              <><Play className="h-4 w-4 mr-2" /> Activate</>
                            )}
                          </DropdownMenuItem>
                          <DropdownMenuItem 
                            onClick={() => deleteCampaign.mutate(campaign.id)}
                            className="text-destructive"
                          >
                            <Trash2 className="h-4 w-4 mr-2" /> Delete
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>

                    <h3 className="font-bold text-lg mb-2 truncate">{campaign.title}</h3>
                    <p className="text-sm text-muted-foreground line-clamp-2 mb-6">
                      {campaign.description || 'No description provided.'}
                    </p>

                    <div className="space-y-3">
                      <div className="flex items-center justify-between text-sm">
                        <div className="flex items-center gap-2">
                          <Target className="h-4 w-4 text-primary" />
                          <span>{campaign.steps?.length || 0} QR Checkpoints</span>
                        </div>
                        <Badge variant="outline" className="text-[10px] font-normal">
                          {campaign.participant_count || 0} participants
                        </Badge>
                      </div>
                      <div className="flex items-center justify-between text-sm">
                        <div className="flex items-center gap-2">
                          <Gift className="h-4 w-4 text-green-500" />
                          <span className="capitalize">{campaign.reward_type} Reward</span>
                        </div>
                        <Badge variant="outline" className="text-[10px] font-normal text-green-600 bg-green-50 border-green-100">
                          {campaign.completion_count || 0} completions
                        </Badge>
                      </div>
                    </div>
                  </div>

                  <div className="px-6 py-4 bg-muted/30 border-t flex justify-between items-center">
                    <span className="text-[10px] text-muted-foreground font-mono">
                      ID: {campaign.id.split('-')[0]}...
                    </span>
                    <Button 
                      variant="link" 
                      size="sm" 
                      className="h-auto p-0"
                      onClick={() => setEditingCampaign(campaign)}
                    >
                      Manage Steps <ExternalLink className="h-3 w-3 ml-1" />
                    </Button>
                  </div>
                </Card>
              </motion.div>
            ))}
          </div>
        )}

        <CreateCampaignModal 
          isOpen={isCreateModalOpen} 
          onClose={() => setIsCreateModalOpen(false)} 
        />
        
        {editingCampaign && (
          <CampaignStepEditor 
            campaign={editingCampaign} 
            onClose={() => setEditingCampaign(null)} 
          />
        )}
      </div>
    </Layout>
  );
}
